import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { contactsApi } from '../services/contactApi';
import { ContactWithDetails, ContactSearchParams } from '../types';
import { debounce } from '../../../../utils/validation';
import { useLoadingState } from '../../../../hooks/useLoadingState';

export const useContacts = () => {
  const [contacts, setContacts] = useState<ContactWithDetails[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isLoading: globalLoading, message: loadingMessage, startLoading, stopLoading } = useLoadingState(250);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });

  const [searchParams, setSearchParams] = useState<ContactSearchParams>({
    search: '',
    page: 1,
    limit: 10,
    sorts: [{
      field: 'dateUpdated',
      direction: 'desc'
    }]
  });
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  const fetchContacts = useCallback(async (params?: ContactSearchParams) => {
    const requestParams = params ?? searchParamsRef.current;
    setLoading(true);
    setError(null);
    startLoading('fetch-contacts', 'Loading contacts...');
    
    try {
      // Build query params manually using URLSearchParams to properly serialize sorts array
      const queryParams = new URLSearchParams();
      
      if (requestParams.search) queryParams.append('search', requestParams.search);
      if (requestParams.page) queryParams.append('page', requestParams.page.toString());
      if (requestParams.limit) queryParams.append('limit', requestParams.limit.toString());
      if (requestParams.typeId !== undefined) queryParams.append('typeId', requestParams.typeId.toString());
      
      // Handle multiple sorts - encode as JSON string
      if (requestParams.sorts && requestParams.sorts.length > 0) {
        queryParams.append('sorts', JSON.stringify(requestParams.sorts));
      }
      
      // Handle multiple search fields - encode as JSON string
      if (requestParams.fields && requestParams.fields.length > 0) {
        queryParams.append('fields', JSON.stringify(requestParams.fields));
      }

      const response = await contactsApi.getAll(queryParams);
      setContacts(response.data);
      setPagination({
        page: response.page,
        limit: response.limit,
        total: response.total,
        totalPages: response.totalPages
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch contacts');
    } finally {
      setLoading(false);
      stopLoading('fetch-contacts');
    }
  }, [startLoading, stopLoading]);

  const debouncedFetchContacts = useMemo(() => debounce(fetchContacts, 300), [fetchContacts]);

  useEffect(() => {
    fetchContacts(searchParams);
  }, []);

  const updateSearchParams = useCallback((newParams: Partial<ContactSearchParams>) => {
    setSearchParams(prevParams => {
      const updatedParams = { ...prevParams, ...newParams };
      
      if (newParams.search !== undefined) {
        // Reset to first page when searching
        updatedParams.page = 1;
        debouncedFetchContacts(updatedParams);
      } else if (newParams.sorts !== undefined) {
        // Reset to first page when sorting
        updatedParams.page = 1;
        fetchContacts(updatedParams);
      } else if (newParams.page !== undefined || newParams.limit !== undefined) {
        // Handle page and limit changes immediately
        fetchContacts(updatedParams);
      } else {
        fetchContacts(updatedParams);
      }
      
      return updatedParams;
    });
  }, [debouncedFetchContacts, fetchContacts]);

  const deleteContact = async (id: number) => {
    startLoading('delete-contact', 'Deleting contact...');
    try {
      await contactsApi.delete(id);
      await fetchContacts();
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete contact');
      return false;
    } finally {
      stopLoading('delete-contact');
    }
  };

  const getContactById = useCallback(async (id: number): Promise<ContactWithDetails | null> => {
    try {
      return await contactsApi.getById(id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch contact details');
      return null;
    }
  }, []);

  return {
    contacts,
    loading,
    error,
    pagination,
    searchParams,
    updateSearchParams,
    deleteContact,
    getContactById,
    refetch: fetchContacts,
    globalLoading,
    loadingMessage
  };
};
